import React, { useState, useMemo } from 'react';
import { makeStyles } from '@material-ui/core/styles';

import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';

import Avatar from '@material-ui/core/Avatar';
import Button from '@material-ui/core/Button';
import Paper from '@material-ui/core/Paper';
import IconButton from '@material-ui/core/IconButton';
import Typography from '@material-ui/core/Typography';
import TextField from '@material-ui/core/TextField';
import CardHeader from '@material-ui/core/CardHeader';

import CloseIcon from '@material-ui/icons/Close';
import UserIcon from '@material-ui/icons/Person';
import AddIcon from '@material-ui/icons/Add';

import Loading from 'app/components/loaders/Loading';

import useApi from 'app/context/useApi';

const useStyles = makeStyles(theme => ({
    closeButton: {
        position: 'absolute',
        right: theme.spacing(1),
        top: theme.spacing(1),
        color: theme.palette.grey[500],
    },
    marginBottom: {
        marginBottom: theme.spacing(3)
    }
}));

export default function EditDateDialog(props) {

    const { open, onSave, onClose, onClosed, invitation } = props;

    const [ dates, setDates ] = useState([]);
    const [ dateName, setDateName ] = useState("");

    const [ loading, setLoading ] = useState(false);
    const [ errors, setErrors ] = useState({});

    const classes = useStyles();
    const api = useApi();

    // Entering Functions ------------------------------------------

    const handleEnter = () => {
        if (invitation?.dates) setDates(invitation.dates);
    }

    // Handler Functions ------------------------------------------

    const handleDateName = e => {
        const value = e.target.value;

        setDateName(value);
    };

    const addDate = () => {
        if (dateName != '') {
            setDates([...dates, {name: dateName}]);
            setDateName('');
        }
    }

    const removeDate = i => {
        const newDates = [...dates];

        if (newDates[i]) newDates.splice(i,1);

        setDates(newDates);
    }

    const datesAllowed = useMemo(() => {
        if (invitation && invitation.ticket) {
            return invitation.ticket.dates_allowed
        } else if (invitation) {
            return invitation.dates_allowed
        } else {
            return 0
        }
    }, [invitation]);

    const datesLeft = (datesAllowed - dates.length);

    // Poster functions ------------------------------------------

    const postDates = () => {

        setLoading(true);

        api.post(`/invitation/${invitation.id}/dates`, {dates: dates})
            .then(() => onSave())
            .catch(error => {
                setLoading(false)
                if (error?.response?.data?.errors) {
                    setErrors(error.response.data.errors)
                }
            });
    };

    // Error Handling -------------------------------------------

    const isError = field => {
        if (errors[field]) return true;
        return false;
    }

    const getErrorMessage = field => {
        if (isError(field)) return errors[field];
        return "";
    }

    // Exting Functions ----------------------------------------

    const clearState = () => {
        setDates([]);
    };

    const handleClosed = () => {
        clearState();
        setLoading(false);
        if (onClosed) onClosed();
    }

    return (
        <Dialog
            aria-labelledby="edit-date-dialog-title"
            open={open}
            aria-describedby="edit-date-dialogue-description"
            fullWidth
            maxWidth="sm"
            disableBackdropClick
            TransitionProps={{
                onExited: handleClosed,
                onEnter: handleEnter
            }}
            >
            <Loading isLoading={loading}/>
            <DialogTitle id="edit-date-dialog-title">
                Edit Dates
                <IconButton aria-label="Cancel" className={classes.closeButton} onClick={onClose}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers id="edit-date-dialog-content">
                <form>
                    <Typography variant="body1" color="textSecondary" className={classes.marginBottom}>
                        Add date(s) to the invitation. Your allowed up to {datesAllowed} date(s).
                    </Typography>
                    {(dates.length > 0) && dates.map((d, i) => (
                        <Paper key={i} elevation={4} className={classes.marginBottom}>
                            <CardHeader
                                avatar={
                                    <Avatar >
                                        <UserIcon />
                                    </Avatar>
                                }
                                title={d.name}
                                action={
                                    <IconButton
                                        aria-label="Remove Date"
                                        onClick={() => removeDate(i)}
                                        >
                                        <CloseIcon />
                                    </IconButton>
                                }
                                className={classes.marginBottom}
                            />
                        </Paper>

                    ))}
                    <CardHeader
                        avatar={
                            <Avatar >
                                <UserIcon />
                            </Avatar>
                        }
                        title={
                            <TextField
                                id="date-name"
                                label="Date Name"
                                value={dateName}
                                onChange={handleDateName}
                                variant="outlined"
                                size="small"
                                fullWidth
                            />
                        }
                        subtitle="Enter date name and press the add button on the right to save."
                        action={
                            <IconButton
                                aria-label="Remove Inviter"
                                onClick={addDate}
                                disabled={(datesLeft < 1)}
                                >
                                <AddIcon />
                            </IconButton>
                        }
                        className={classes.marginBottom}
                    />
                    <Typography variant="subtitle1" color="primary" gutterBottom>
                        <strong>{dates.length}</strong> date(s) added to invitation.
                    </Typography>
                </form>
            </DialogContent>
            <DialogActions>
                <Button onClick={postDates} color="primary" disabled={false} variant="contained">
                    Save Dates
                </Button>
            </DialogActions>
        </Dialog>
    );
}